import {
    Button,
    GuaranteeStatus,
    IReservation,
    IReservationGuarantee,
    PaymentUtils,
    Pill,
    ReservationStatus,
    Spinner,
    TPillTone,
    useConfirmContext,
} from '@localina/core';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHaveAccountFeatures } from '../../api/queries/account';
import { useGuaranteeActionMutation } from '../../api/queries/guarantees';
import { usePaymentAccount } from '../../api/queries/payments';
import { useRestaurant } from '../../api/queries/restaurants';
import { LocalinaApiContext } from '../../../index';
import { useReservationFormContext } from '../../contexts/ReservationsContext';
import { ReservationDetailsType } from '../../enums';
import { TGuaranteeAction } from '../../interfaces';
import { useRestaurantId } from '../../utils/RestaurantUtils';
import { getFreeCancellationUntil, isNoShowTooEarly } from './guaranteeUtils';

// No-Show Protection, parts 5 and 6: the guarantee of a reservation in the reservation drawer, under the status
// switch. Charging is deliberately manual: only after the reservation is saved as No-Show (saving it asks right away,
// see NoShowChargeDialog), and only by the host, Chef de Service or support (feature "noShowCharge").

interface IProps {
    reservation: IReservation;
    /** the status selected in the form (may not be saved yet) */
    selectedStatus?: ReservationStatus;
    /** e-mail / mobile currently in the form (may not be saved yet); a request goes to the SAVED contact details */
    guestEmail?: string;
    guestPhone?: string;
}

const STATUS_TONE: Record<GuaranteeStatus, TPillTone> = {
    [GuaranteeStatus.NOT_REQUESTED]: 'neutral',
    [GuaranteeStatus.REQUESTED]: 'warn',
    [GuaranteeStatus.SECURED]: 'teal',
    [GuaranteeStatus.RELEASED]: 'neutral',
    [GuaranteeStatus.CHARGED]: 'good',
    [GuaranteeStatus.CHARGE_FAILED]: 'critical',
    [GuaranteeStatus.REFUNDED]: 'neutral',
    [GuaranteeStatus.WAIVED]: 'neutral',
};

export const GuaranteeBlock: React.FC<IProps> = (props) => {
    const { t, i18n } = useTranslation();
    const { confirm, snackbar } = useConfirmContext();
    const restaurantQuery = useRestaurant();
    const [canCharge] = useHaveAccountFeatures(['noShowCharge']);
    const restaurantId = useRestaurantId();
    const { openReservationForm } = useReservationFormContext();
    // the drawer keeps the reservation it was opened with, so the block follows the action results itself
    const [guarantee, setGuarantee] = useState<IReservationGuarantee | null | undefined>(props.reservation.guarantee);
    useEffect(() => setGuarantee(props.reservation.guarantee), [props.reservation.guarantee]);
    // a dispute is answered in the payment provider's dashboard (only admins can open the account, others get no link)
    const dispute = guarantee?.disputeStatus ?? null;
    const accountQuery = usePaymentAccount({ enabled: dispute === 'OPEN', retry: false });

    const requestMutation = useGuaranteeActionMutation('request');
    const chargeMutation = useGuaranteeActionMutation('charge');
    const refundMutation = useGuaranteeActionMutation('refund');
    const waiveMutation = useGuaranteeActionMutation('waive');
    const mutations: Record<TGuaranteeAction, typeof chargeMutation> = {
        request: requestMutation,
        charge: chargeMutation,
        refund: refundMutation,
        waive: waiveMutation,
    };
    const isLoading = Object.values(mutations).some((mutation) => mutation.isLoading);

    if (!guarantee) {
        return null;
    }

    const money = (amount?: number) => PaymentUtils.formatAmount(amount, guarantee.currency || 'chf', i18n.language);
    const dateTime = (iso?: string) =>
        iso ? DateTime.fromISO(iso).setLocale(i18n.language).toFormat('dd.MM.yyyy HH:mm') : '–';
    const amount = money(guarantee.amount);
    const savedStatus = props.reservation.status;
    const guestName = [props.reservation.guestInfo?.firstName, props.reservation.guestInfo?.lastName]
        .filter(Boolean)
        .join(' ');
    const cancelNotice = restaurantQuery.data?.configuration.cancelReservationNotice;
    const shownReservation = { ...props.reservation, guarantee };
    const freeCancellationUntil = getFreeCancellationUntil(shownReservation, cancelNotice);
    const noShowTooEarly = isNoShowTooEarly(shownReservation, cancelNotice);
    const calculation =
        guarantee.calculation === 'PER_PERSON'
            ? t('reservations.guarantee.perPersonCalculation', {
                  unit: money(guarantee.unitAmount),
                  persons: guarantee.participants,
              })
            : t('reservations.guarantee.perReservationCalculation');
    const method =
        guarantee.methodType === 'TWINT'
            ? t('integrations.items.payments.methods.TWINT')
            : guarantee.methodLast4
            ? `${capitalize(guarantee.methodBrand)} •••• ${guarantee.methodLast4}${
                  guarantee.methodExpiry ? ` · ${guarantee.methodExpiry}` : ''
              }`
            : undefined;
    // the backend sends the request to the saved contact details, so that's what is shown and checked
    const savedEmail = props.reservation.guestInfo?.email || '';
    const savedPhone = props.reservation.guestInfo?.phoneNumbers?.[0] || '';
    const channels = [
        savedEmail ? t('reservations.guarantee.channels.EMAIL') : null,
        savedPhone ? t('reservations.guarantee.channels.SMS') : null,
    ].filter(Boolean);
    const contactChanged =
        (props.guestEmail ?? savedEmail) !== savedEmail || (props.guestPhone ?? savedPhone) !== savedPhone;
    // only an upcoming confirmed reservation can still be secured
    const canRequest =
        savedStatus === ReservationStatus.APPROVED &&
        DateTime.fromISO(props.reservation.reservationDateTime) > DateTime.now();

    // the drawer was opened with a snapshot of the reservation: after an action, reopen it with the current one, so
    // the status in the form (held -> confirmed / released) and the guarantee match what the backend did
    const refreshReservation = async () => {
        try {
            const current = await LocalinaApiContext.serviceApi.getReservation(restaurantId, props.reservation.id);
            setGuarantee(current.guarantee);
            openReservationForm(current, ReservationDetailsType.EDIT);
        } catch {
            // keep the snapshot; the list reloads anyway
        }
    };

    const run = async (action: TGuaranteeAction, successKey: string) => {
        try {
            const result = await mutations[action].mutateAsync({ reservationId: props.reservation.id });
            setGuarantee(result);
            if (result.status === GuaranteeStatus.CHARGE_FAILED) {
                snackbar({ msg: t('reservations.guarantee.messages.chargeFailed'), severity: 'error' });
            } else {
                snackbar({ msg: t(successKey), severity: 'success', autoHideDuration: 2500 });
            }
        } catch {
            snackbar({ msg: t('reservations.guarantee.messages.error'), severity: 'error' });
        }
        // also after an error: the action may have gone through (e.g. a charge that timed out here)
        await refreshReservation();
    };

    // every action that moves money or gives it up is confirmed first
    const confirmAndRun = async (dialog: string, action: TGuaranteeAction, successKey: string) => {
        const answer = await confirm({
            title: t(`reservations.guarantee.${dialog}.title`),
            msg: t(`reservations.guarantee.${dialog}.message`, { amount, name: guestName }),
            confirmLabel: t(`reservations.guarantee.${dialog}.confirm`),
        });
        if (answer === 'yes') {
            await run(action, successKey);
        }
    };

    const charge = () => confirmAndRun('chargeDialog', 'charge', 'reservations.guarantee.messages.charged');
    const refund = () => confirmAndRun('refundDialog', 'refund', 'reservations.guarantee.messages.refunded');
    const waive = () => confirmAndRun('waiveDialog', 'waive', 'reservations.guarantee.messages.waived');
    const confirmWithout = () => confirmAndRun('withoutDialog', 'waive', 'reservations.guarantee.messages.waived');

    const methodLabel = guarantee.methodType === 'TWINT' ? 'twintSecured' : 'cardSecured';
    const statusLabel =
        dispute === 'OPEN'
            ? t('reservations.guarantee.badge.disputed')
            : dispute === 'LOST'
            ? t('reservations.guarantee.badge.disputeLost')
            : guarantee.status === GuaranteeStatus.SECURED
            ? t(`reservations.guarantee.status.${methodLabel}`)
            : t(`reservations.guarantee.status.${guarantee.status}`);
    // same as the list badge: an open dispute needs an answer, a lost one is only information
    const statusTone: TPillTone =
        dispute === 'OPEN' ? 'critical' : dispute === 'LOST' ? 'warn' : STATUS_TONE[guarantee.status];

    const chargeOnlyHint = !canCharge && (
        <div className="guarantee-block__note neutral">{t('reservations.guarantee.chefOnly')}</div>
    );

    return (
        <div className="guarantee-block">
            <div className="guarantee-block__title">
                <span>{t('reservations.guarantee.title')}</span>
                <Pill tone={statusTone} label={statusLabel} />
            </div>

            <dl className="guarantee-block__facts">
                <dt>
                    {t(
                        guarantee.status === GuaranteeStatus.NOT_REQUESTED
                            ? 'reservations.guarantee.fields.proposedAmount'
                            : 'reservations.guarantee.fields.amount',
                    )}
                </dt>
                <dd>{amount}</dd>
                <dt>{t('reservations.guarantee.fields.calculation')}</dt>
                <dd>{calculation}</dd>
                {method && (
                    <>
                        <dt>{t('reservations.guarantee.fields.method')}</dt>
                        <dd>{method}</dd>
                    </>
                )}
                {guarantee.securedAt && (
                    <>
                        <dt>{t('reservations.guarantee.fields.securedAt')}</dt>
                        <dd>{dateTime(guarantee.securedAt)}</dd>
                    </>
                )}
                {guarantee.status === GuaranteeStatus.NOT_REQUESTED && (
                    <>
                        <dt>{t('reservations.guarantee.fields.sendTo')}</dt>
                        <dd>{channels.length ? channels.join(' + ') : '–'}</dd>
                    </>
                )}
                {guarantee.status === GuaranteeStatus.REQUESTED && (
                    <>
                        <dt>{t('reservations.guarantee.fields.requestedAt')}</dt>
                        <dd>{dateTime(guarantee.requestedAt)}</dd>
                        <dt>{t('reservations.guarantee.fields.deadline')}</dt>
                        <dd>{dateTime(guarantee.deadline)}</dd>
                    </>
                )}
                {freeCancellationUntil &&
                    [GuaranteeStatus.SECURED, GuaranteeStatus.REQUESTED].includes(guarantee.status) && (
                        <>
                            <dt>{t('reservations.guarantee.fields.freeCancellationUntil')}</dt>
                            <dd>{dateTime(freeCancellationUntil)}</dd>
                        </>
                    )}
                {guarantee.chargedAt && (
                    <>
                        <dt>{t('reservations.guarantee.fields.chargedAt')}</dt>
                        <dd>
                            {dateTime(guarantee.chargedAt)}
                            {guarantee.chargedBy ? ` · ${guarantee.chargedBy}` : ''}
                        </dd>
                    </>
                )}
            </dl>

            {guarantee.status === GuaranteeStatus.NOT_REQUESTED && canRequest && (
                <>
                    <div className="guarantee-block__note neutral">
                        {guarantee.failureMessage
                            ? t('reservations.guarantee.notes.revoked')
                            : t('reservations.guarantee.notes.notRequested', { amount })}
                    </div>
                    {!savedEmail && (
                        <div className="guarantee-block__note warn">{t('reservations.guarantee.notes.noContact')}</div>
                    )}
                    {contactChanged && (
                        <div className="guarantee-block__note warn">
                            {t('reservations.guarantee.notes.saveContactFirst')}
                        </div>
                    )}
                    <div className="guarantee-block__actions">
                        <Button
                            label={t('reservations.guarantee.actions.request')}
                            disabled={isLoading || !savedEmail || contactChanged}
                            onClick={() => run('request', 'reservations.guarantee.messages.requested')}
                        />
                    </div>
                </>
            )}

            {guarantee.status === GuaranteeStatus.REQUESTED && (
                <>
                    <div className="guarantee-block__note warn">
                        {t('reservations.guarantee.notes.waiting', { deadline: dateTime(guarantee.deadline) })}
                    </div>
                    {guarantee.failureMessage && (
                        <div className="guarantee-block__note crit">{guarantee.failureMessage}</div>
                    )}
                    {/* the link goes out once; letting the table go is a normal cancellation (PO, 22.10.) */}
                    <div className="guarantee-block__actions">
                        <Button
                            secondary
                            label={t('reservations.guarantee.actions.confirmWithout')}
                            disabled={isLoading}
                            onClick={confirmWithout}
                        />
                    </div>
                </>
            )}

            {guarantee.status === GuaranteeStatus.SECURED && savedStatus !== ReservationStatus.NO_SHOW && (
                <div className="guarantee-block__note neutral">
                    {props.selectedStatus === ReservationStatus.FULFILLED
                        ? t('reservations.guarantee.notes.arrived')
                        : noShowTooEarly
                        ? t('reservations.guarantee.notes.noShowFrom', { time: dateTime(freeCancellationUntil) })
                        : props.selectedStatus === ReservationStatus.NO_SHOW
                        ? t('reservations.guarantee.notes.saveNoShowFirst')
                        : t('reservations.guarantee.notes.notChargeable')}
                </div>
            )}

            {[GuaranteeStatus.SECURED, GuaranteeStatus.CHARGE_FAILED].includes(guarantee.status) &&
                savedStatus === ReservationStatus.NO_SHOW && (
                    <>
                        {/* "you can now charge" is only true for who may charge; the others get chargeOnlyHint */}
                        {(canCharge || guarantee.status === GuaranteeStatus.CHARGE_FAILED) && (
                            <div className="guarantee-block__note crit">
                                {guarantee.status === GuaranteeStatus.CHARGE_FAILED
                                    ? t('reservations.guarantee.notes.chargeFailed', {
                                          // provider messages end with a full stop; the sentence around them adds its own
                                          reason: (guarantee.failureMessage ?? '').replace(/\.\s*$/, ''),
                                      })
                                    : t('reservations.guarantee.notes.noShow')}
                            </div>
                        )}
                        {chargeOnlyHint}
                        {/* giving up a fee that could be charged is a money decision too (PO, 3.11.) */}
                        {canCharge && (
                            <div className="guarantee-block__actions">
                                <Button
                                    label={t(
                                        guarantee.status === GuaranteeStatus.CHARGE_FAILED
                                            ? 'reservations.guarantee.actions.retry'
                                            : 'reservations.guarantee.actions.charge',
                                        { amount },
                                    )}
                                    disabled={isLoading}
                                    onClick={charge}
                                />
                                <Button
                                    secondary
                                    label={t('reservations.guarantee.actions.waive')}
                                    disabled={isLoading}
                                    onClick={waive}
                                />
                            </div>
                        )}
                    </>
                )}

            {guarantee.status === GuaranteeStatus.CHARGED && (
                <>
                    <div className="guarantee-block__note ok">
                        {t('reservations.guarantee.notes.charged', { amount, date: dateTime(guarantee.chargedAt) })}
                    </div>
                    {guarantee.failureMessage && (
                        <div className="guarantee-block__note warn">{guarantee.failureMessage}</div>
                    )}
                    {dispute && (
                        <div
                            className={`guarantee-block__note ${
                                dispute === 'OPEN' ? 'crit' : dispute === 'LOST' ? 'warn' : 'ok'
                            }`}
                        >
                            <b>{t(`reservations.guarantee.dispute.${dispute}.title`)}</b>{' '}
                            {t(`reservations.guarantee.dispute.${dispute}.text`, {
                                amount,
                                reason: t(`reservations.guarantee.dispute.reasons.${guarantee.disputeReason ?? ''}`, {
                                    defaultValue: t('reservations.guarantee.dispute.reasons.general'),
                                }),
                            })}
                            {dispute === 'OPEN' && accountQuery.data?.dashboardUrl && (
                                <>
                                    {' '}
                                    <a href={accountQuery.data.dashboardUrl} target="_blank" rel="noopener noreferrer">
                                        {t('reservations.guarantee.dispute.respond')}
                                    </a>
                                </>
                            )}
                        </div>
                    )}
                    {/* a disputed charge can't be refunded while the bank decides, a lost one went back already */}
                    {canCharge && dispute !== 'OPEN' && dispute !== 'LOST' && (
                        <div className="guarantee-block__actions">
                            <Button
                                secondary
                                label={t('reservations.guarantee.actions.refund')}
                                disabled={isLoading}
                                onClick={refund}
                            />
                        </div>
                    )}
                </>
            )}

            {guarantee.status === GuaranteeStatus.REFUNDED && (
                <div className="guarantee-block__note neutral">
                    {t('reservations.guarantee.notes.refunded', { amount, date: dateTime(guarantee.refundedAt) })}
                </div>
            )}
            {guarantee.status === GuaranteeStatus.RELEASED && (
                <div className="guarantee-block__note neutral">{t('reservations.guarantee.notes.released')}</div>
            )}
            {guarantee.status === GuaranteeStatus.WAIVED && (
                <div className="guarantee-block__note neutral">
                    {t('reservations.guarantee.notes.waived', {
                        date: dateTime(guarantee.waivedAt),
                        name: guarantee.waivedBy ?? '–',
                    })}
                </div>
            )}
            {isLoading && <Spinner />}
        </div>
    );
};

const capitalize = (value?: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');
